// src/pages/Admin/ReglementInscription/ReglementInscriptionPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import EditReglementInscriptionModal from "./EditReglementInscriptionModal";
import CreateReglementInscriptionModal from "./CreateReglementInscriptionModal";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import {
  getAllReglementInscription,
  deleteReglementInscription,
  updateReglementInscription,
} from "../../../services/reglementinscriptionServices";
import { getSettings } from "../../../services/settingsServices";
import { getGroupsList } from "../../../services/groupServices";
import {
  getAllDossiersInscription,
  updateDossierInscription,
} from "../../../services/dossierServices"; // ⬅️ AJOUT

import Button from "./../../../components/Button/Button";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import ButtonCBplus from "./../../../components/Button/ButtonCBplus";
import ButtonPrint from "./../../../components/Button/ButtonPrint";
import { FaPlusSquare } from "react-icons/fa";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReglementCharts from "./ReglementCharts";

const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

export default function ReglementInscriptionPage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const navigate = useNavigate();

  const [reglements, setReglements] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔗 Dossier par (adherentId + saison)
  // key = `${adherentId}:${saison}` -> value = dossierId
  const [dossierIndex, setDossierIndex] = useState({}); // ⬅️ AJOUT

  // Filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [passSportFilter, setPassSportFilter] = useState("");
  // Multi-statuts + dropdown
  const [statutReglementFilters, setStatutReglementFilters] = useState([]);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");

  // Actions / UI
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editableReglement, setEditableReglement] = useState(null);
  const [clubSettings, setClubSettings] = useState(null);
  const [showCharts, setShowCharts] = useState(false);

  // Virtualisation simple
  const CHUNK = 50;
  const [visibleCount, setVisibleCount] = useState(CHUNK);
  const scrollRef = useRef(null);

  // Toggle statut_reglement
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusDraft, setStatusDraft] = useState("");
  const [savingStatusId, setSavingStatusId] = useState(null);

  const STATUTS = [
    "Réglé",
    "Partiellement réglé",
    "En cours",
    "Non réglé",
    "Problème",
    "Annulé",
  ];

  const getStatusClass = (s) =>
    s === "Réglé"
      ? "bg-emerald-600"
      : s === "En cours"
        ? "bg-amber-500"
        : s === "Non réglé"
          ? "bg-slate-700"
          : s === "Partiellement réglé"
            ? "bg-amber-500"
            : s === "Problème"
              ? "bg-rose-600"
              : "bg-slate-400";

  // Debounce recherche
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(searchTerm.trim().toLowerCase()),
      250,
    );
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchReglements = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllReglementInscription();
      setReglements(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des règlements.");
    } finally {
      setLoading(false);
    }
  }, []);

  // ⬇️ Récupère settings (saison active) + groups
  const fetchSettingsData = useCallback(async () => {
    try {
      const settings = await getSettings();
      if (settings?.length) {
        const active = settings[0].saison_active;
        const [y1, y2] = active.split("-").map(Number);
        const seasons3 = [`${y1 - 1}-${y2 - 1}`, active, `${y1 + 1}-${y2 + 1}`];
        setSeasons(seasons3);
        setSelectedSeason(active);
        setClubSettings(settings[0]);
      }
    } catch (e) {
      console.error("Erreur lors du chargement des paramètres:", e);
    }
  }, []);

  const fetchGroupsData = useCallback(async () => {
    try {
      const gl = await getGroupsList();
      setGroups(Array.isArray(gl) ? gl : []);
    } catch (e) {
      console.error("Erreur lors du chargement des groupes:", e);
    }
  }, []);

  // ⬇️ Construit l'index des dossiers par (adherentId + saison)
  const buildDossierIndex = useCallback(async () => {
    try {
      const dossiers = await getAllDossiersInscription();
      // On indexe sur la saison qui est portée par l'Adherent (saison_active)
      // key = `${inscription_adherent_id}:${Adherent.saison_active}`
      const idx = {};
      (Array.isArray(dossiers) ? dossiers : []).forEach((d) => {
        const adherentId = d?.inscription_adherent_id || d?.Adherent?.id;
        const saison = d?.Adherent?.saison_active;
        if (adherentId && saison) {
          idx[`${adherentId}:${saison}`] = d.id;
        }
      });
      setDossierIndex(idx);
    } catch (e) {
      console.error("Erreur lors de l'indexation des dossiers:", e);
      // pas de toast bloquant ici
    }
  }, []);

  useEffect(() => {
    fetchReglements();
    fetchSettingsData();
    fetchGroupsData();
    buildDossierIndex(); // ⬅️ on charge l'index une fois au montage
  }, [fetchReglements, fetchSettingsData, fetchGroupsData, buildDossierIndex]);

  const calculateTotal = useCallback((r) => {
    const lic = Number(r?.montant_lic_assurance) || 0;
    const cot = Number(r?.montant_cotisation) || 0;
    const redF = Number(r?.reduction_foyer) || 0;
    const pass = Number(r?.pass_sport) || 0;
    const passC32 = Number(r?.passclub32) || 0;
    const redS = Number(r?.reduction_saison) || 0;
    return lic + cot + redF + pass + passC32 + redS;
  }, []);

  // Options de Pass'Sport dérivées des données (uniques triées)
  const passSportOptions = useMemo(() => {
    // 1) on convertit d’abord en nombre
    // 2) on filtre les NaN
    // 3) on déduplique avec Set *après* normalisation
    const nums = reglements
      .map((r) => Number(r?.pass_sport))
      .filter((v) => Number.isFinite(v));
    return Array.from(new Set(nums)).sort((a, b) => a - b);
  }, [reglements]);

  // Filtrage
  const filtered = useMemo(() => {
    return reglements.filter((r) => {
      const a = r?.Adherent || {};
      const matchSearch =
        !debouncedSearch ||
        `${a.nom || ""} ${a.prenom || ""}`
          .toLowerCase()
          .includes(debouncedSearch);

      const matchSeason = !selectedSeason || r?.saison === selectedSeason;
      const matchPass =
        passSportFilter === ""
          ? true
          : passSportFilter === "nonzero"
            ? Number(r?.pass_sport) !== 0
            : Number(r?.pass_sport) === Number(passSportFilter);

      const matchStatut =
        statutReglementFilters.length === 0 ||
        statutReglementFilters.includes(r?.statut_reglement);

      const matchGroup =
        selectedGroup === "" ||
        (a?.Groups &&
          a.Groups.some((g) => String(g?.id) === String(selectedGroup)));

      return (
        matchSearch && matchSeason && matchPass && matchStatut && matchGroup
      );
    });
  }, [
    reglements,
    debouncedSearch,
    selectedSeason,
    passSportFilter,
    statutReglementFilters,
    selectedGroup,
  ]);

  // Totaux affichés
  const totalAffiche = useMemo(
    () => filtered.reduce((s, r) => s + calculateTotal(r), 0),
    [filtered, calculateTotal],
  );

  const visibleReglements = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset scroll / chunk sur changement de filtres
  useEffect(() => {
    setVisibleCount(CHUNK);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [
    debouncedSearch,
    selectedSeason,
    passSportFilter,
    statutReglementFilters,
    selectedGroup,
  ]);

  // Charger plus au scroll
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
      setVisibleCount((v) =>
        v >= filtered.length ? v : Math.min(v + CHUNK, filtered.length),
      );
    }
  };

  // CRUD
  const openEditModal = (reglement) => {
    setEditableReglement(reglement);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    fetchReglements();
    setIsEditModalOpen(false);
  };
  const closeCreateModal = () => {
    fetchReglements();
    setIsCreateModalOpen(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce règlement ?")) return;
    try {
      await deleteReglementInscription(id);
      toast.success("Règlement supprimé.");
      fetchReglements();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression.");
    }
  };

  // ⬇️ Sauvegarde statut + synchro dossier.validate_reglement
  const saveStatus = async (reglement, newStatus) => {
    const id =
      reglement?.id ??
      reglement?.reglement_id ??
      reglement?.ReglementInscription?.id;
    if (!id) {
      toast.error("ID du règlement introuvable.");
      return;
    }

    try {
      setSavingStatusId(id);

      // 1) MAJ du statut de règlement d'inscription
      await updateReglementInscription({ id, statut_reglement: newStatus });

      // 2) MAJ du dossier d'inscription lié à l'adhérent & à la saison du règlement
      const adherentId = reglement?.Adherent?.id || reglement?.adherent_id;
      const saison = reglement?.saison || selectedSeason; // on privilégie la saison du règlement
      if (adherentId && saison) {
        const key = `${adherentId}:${saison}`;
        const dossierId = dossierIndex[key];

        // on considère le "validé" si Réglé ou Partiellement réglé
        const validated =
          newStatus === "Réglé" || newStatus === "Partiellement réglé";

        if (dossierId) {
          try {
            await updateDossierInscription(dossierId, {
              validate_reglement: validated,
            });
          } catch (e) {
            console.error("Erreur MAJ dossier.validate_reglement:", e);
            // on n'empêche pas la MAJ principale, mais on informe
            toast.error(
              "Statut mis à jour, mais la synchronisation du dossier a échoué.",
            );
          }
        } else {
          // Pas trouvé dans l'index → on tente un rebuild rapide (au cas où un dossier a été créé après)
          await buildDossierIndex();
          const refreshedId = ((prev) => prev[`${adherentId}:${saison}`])(
            // simple lookup après rebuild
            await (async () => {
              const dossiers = await getAllDossiersInscription();
              const idx = {};
              (Array.isArray(dossiers) ? dossiers : []).forEach((d) => {
                const aId = d?.inscription_adherent_id || d?.Adherent?.id;
                const s = d?.Adherent?.saison_active;
                if (aId && s) idx[`${aId}:${s}`] = d.id;
              });
              setDossierIndex(idx);
              return idx;
            })(),
          );
          if (refreshedId) {
            try {
              await updateDossierInscription(refreshedId, {
                validate_reglement: validated,
              });
            } catch (e) {
              console.error(
                "Erreur MAJ dossier.validate_reglement (rebuild):",
                e,
              );
              toast.error(
                "Statut mis à jour, mais la synchronisation du dossier a échoué.",
              );
            }
          } else {
            console.warn(
              "Aucun dossier trouvé pour",
              adherentId,
              "saison",
              saison,
            );
          }
        }
      }

      toast.success("Statut mis à jour.");
      setEditingStatusId(null);
      fetchReglements();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la mise à jour du statut.");
    } finally {
      setSavingStatusId(null);
    }
  };

  // Attestation
  const handleOpenAttestation = (reglement) => {
    const a = reglement?.Adherent || {};
    const s = clubSettings || {};
    navigate("/attestation", {
      state: {
        prenom: a.prenom || "",
        nom: a.nom || "",
        dateNaissance: a.date_naissance
          ? new Date(a.date_naissance).toLocaleDateString()
          : "",
        adresse: a.adresse || "",
        cp: a.cp || "",
        commune: a.commune || "",
        cotisation: Number(reglement?.montant_cotisation) || 0,
        licence: Number(reglement?.montant_lic_assurance) || 0,
        reductionFoyer: Number(reglement?.reduction_foyer) || 0,
        passSport: Number(reglement?.pass_sport) || 0,
        passClub32: Number(reglement?.passclub32) || 0,
        reductionSaison: Number(reglement?.reduction_saison) || 0,
        typeReglement: reglement?.type_reglement || "",
        nbReglement: Number(reglement?.nb_reglement) || 0,
        statutReglement: reglement?.statut_reglement || "",
        observations: reglement?.observations || "",
        datePaiement: new Date().toLocaleDateString(),
        president: s.responsable_club || "",
        association: s.nom_club || "",
        logo: s.logo_club || "",
        adresse_club: s.adresse_club || "",
        tel_club: s.tel_club || "",
        mail_club: s.mail_club || "",
        siteweb_club: s.siteweb_club || "",
        siret_club: s.siret_club || "",
        saison_active: s.saison_active || selectedSeason,
      },
    });
  };

  // Export CSV
  const exportCSV = () => {
    const header = [
      "Nom",
      "Prénom",
      "Saison",
      "Statut",
      "Type",
      "Nb",
      "Lic-Ass",
      "Cotisation",
      "Réduction Foyer",
      "Pass Sport",
      "Réduction Saison",
      "Total",
      "Groupes",
      "Observations",
    ];
    const rows = filtered.map((r) => {
      const a = r?.Adherent || {};
      const groups = (a?.Groups || []).map((g) => g?.nom).join(", ");
      const total = calculateTotal(r);
      const vals = [
        a?.nom || "",
        a?.prenom || "",
        r?.saison || "",
        r?.statut_reglement || "",
        r?.type_reglement || "",
        r?.nb_reglement ?? "",
        r?.montant_lic_assurance ?? "",
        r?.montant_cotisation ?? "",
        r?.reduction_foyer ?? "",
        r?.pass_sport ?? "",
        r?.passclub32 ?? "",
        r?.reduction_saison ?? "",
        total.toFixed(2),
        groups,
        r?.observations || "",
      ];
      return vals.map((s) => `"${String(s).replace(/"/g, '""')}"`).join(";");
    });
    const csv = [header.join(";"), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "reglements_inscription.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Règlements Inscriptions"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé + actions */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Suivi des règlements de Licences-Cotisations-Réductions
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Ici, vous pouvez gérer les règlements des adhérents pour la
                saison {selectedSeason}
              </p>

              {/* Actions */}
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <Button
                  text="Créer un règlement"
                  icon={FaPlusSquare}
                  onClick={() => setIsCreateModalOpen(true)}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-2"
                />
                <Button
                  text={
                    showCharts
                      ? "Masquer les graphiques"
                      : "Afficher les graphiques"
                  }
                  onClick={() => setShowCharts((v) => !v)}
                  className="!bg-white/90 !text-slate-900 !hover:bg-white !rounded-xl !shadow-md"
                />
                <Button
                  text="Exporter CSV (filtres)"
                  onClick={exportCSV}
                  className="!bg-white/90 !text-slate-900 !hover:bg-white !rounded-xl !shadow-md"
                />
              </div>
            </div>
          </div>

          {/* Toolbar filtres */}
          <div className="px-5 py-5 sm:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
              <input
                type="text"
                placeholder="Rechercher (nom, prénom)…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full xl:col-span-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />

              <select
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                {seasons.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              <select
                value={passSportFilter}
                onChange={(e) => setPassSportFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Pass’Sport (tous)</option>
                {passSportOptions.map((v) => (
                  <option key={String(v)} value={String(v)}>
                    {fmtEUR(v)}
                  </option>
                ))}
              </select>

              {/* Filtre multi-statuts */}
              <div className="relative" ref={statusDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsStatusDropdownOpen((v) => !v)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-violet-400"
                  title="Filtrer par plusieurs statuts"
                >
                  {statutReglementFilters.length === 0
                    ? "Statuts (tous)"
                    : `Statuts (${statutReglementFilters.length})`}
                </button>

                {isStatusDropdownOpen && (
                  <div className="absolute z-50 mt-2 w-72 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => setStatutReglementFilters([...STATUTS])}
                        className="text-xs px-2 py-1 rounded-md ring-1 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        Tout
                      </button>
                      <button
                        type="button"
                        onClick={() => setStatutReglementFilters([])}
                        className="text-xs px-2 py-1 rounded-md ring-1 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        Effacer
                      </button>
                    </div>

                    <ul className="max-h-60 overflow-auto space-y-1 pr-1">
                      {STATUTS.map((s) => {
                        const id = `status-${s
                          .replace(/\s+/g, "-")
                          .toLowerCase()}`;
                        const checked = statutReglementFilters.includes(s);
                        return (
                          <li key={s} className="flex items-center gap-2">
                            <input
                              id={id}
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setStatutReglementFilters((prev) =>
                                    prev.includes(s) ? prev : [...prev, s],
                                  );
                                } else {
                                  setStatutReglementFilters((prev) =>
                                    prev.filter((x) => x !== s),
                                  );
                                }
                              }}
                              className="h-4 w-4 rounded border-slate-300 dark:border-slate-700"
                            />
                            <label
                              htmlFor={id}
                              className="text-sm select-none text-slate-800 dark:text-slate-200"
                            >
                              {s}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Tous les groupes</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.nom}
                  </option>
                ))}
              </select>

              <div className="hidden md:block" />
            </div>

            {/* Compteurs entre filtres et tableau */}
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-700 dark:text-slate-200">
              <span>
                Adhérents affichés :{" "}
                <span className="font-semibold">{filtered.length}</span>
              </span>
              <span className="inline-flex items-center rounded-md px-2 py-0.5 ring-1 ring-slate-300 dark:ring-slate-700 bg-white/70 dark:bg-slate-900/60">
                Total affiché :{" "}
                <span className="ml-1 font-semibold">
                  {fmtEUR(totalAffiche)}
                </span>
              </span>
            </div>
          </div>

          {/* Graphiques (toggle) */}
          {showCharts && (
            <div className="px-5 sm:px-8 pb-5">
              <ReglementCharts data={filtered} season={selectedSeason} />
            </div>
          )}

          {/* Contenu responsive : cartes mobile + tableau compact desktop */}
          <div className="px-5 pb-6 sm:px-8">
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun règlement
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajoutez un règlement ou modifiez vos filtres.
                </p>
              </div>
            )}

            {!loading && filtered.length > 0 && (
              <>
                {/* ================= MOBILE / TABLETTE ================= */}
                <div className="lg:hidden space-y-4">
                  {visibleReglements.map((r) => {
                    const a = r?.Adherent || {};
                    const total = calculateTotal(r);

                    return (
                      <article
                        key={r.id}
                        className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 shadow-sm p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                              {a.nom || "—"} {a.prenom || ""}
                            </h3>
                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              Saison : {r.saison || "—"}
                            </div>
                          </div>

                          <div className="text-right">
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              Total
                            </div>
                            <div className="text-base font-bold text-slate-900 dark:text-white">
                              {fmtEUR(total)}
                            </div>
                          </div>
                        </div>

                        <div className="mt-3">
                          {editingStatusId === r.id ? (
                            <select
                              autoFocus
                              value={statusDraft || ""}
                              disabled={savingStatusId === r.id}
                              onChange={(e) => {
                                const v = e.target.value;
                                setStatusDraft(v);
                                saveStatus(r, v);
                              }}
                              onBlur={() => setEditingStatusId(null)}
                              onKeyDown={(e) => {
                                if (e.key === "Escape")
                                  setEditingStatusId(null);
                              }}
                              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                            >
                              <option value="">—</option>
                              {STATUTS.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingStatusId(r.id);
                                setStatusDraft(r.statut_reglement || "");
                              }}
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white ${getStatusClass(
                                r.statut_reglement,
                              )}`}
                              title="Modifier le statut"
                            >
                              {savingStatusId === r.id
                                ? "…"
                                : r.statut_reglement || "—"}
                            </button>
                          )}
                        </div>

                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-3">
                            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                              Montants
                            </div>
                            <div className="mt-2 space-y-1 text-slate-700 dark:text-slate-200">
                              <div className="flex justify-between gap-3">
                                <span>Licence + assurance</span>
                                <span className="font-medium">
                                  {fmtEUR(r.montant_lic_assurance)}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span>Cotisation</span>
                                <span className="font-medium">
                                  {fmtEUR(r.montant_cotisation)}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span>Réduction foyer</span>
                                <span className="font-medium">
                                  {fmtEUR(r.reduction_foyer)}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span>Pass’Sport</span>
                                <span className="font-medium">
                                  {r.pass_sport !== null &&
                                  r.pass_sport !== undefined
                                    ? fmtEUR(r.pass_sport)
                                    : "N/A"}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span>Pass Club 32</span>
                                <span className="font-medium">
                                  {r.passclub32 !== null && r.passclub32 !== undefined
                                    ? fmtEUR(r.passclub32)
                                    : "N/A"}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span>Réduction saison</span>
                                <span className="font-medium">
                                  {fmtEUR(r.reduction_saison)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-3 space-y-3">
                            <div>
                              <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Paiement
                              </div>
                              <div className="mt-1 text-slate-800 dark:text-slate-100">
                                {r.type_reglement || "—"}
                                <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                                  ({r.nb_reglement ?? "—"} règlement(s))
                                </span>
                              </div>
                            </div>

                            <div>
                              <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Groupes
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {(a?.Groups || []).length ? (
                                  a.Groups.map((g) => (
                                    <span
                                      key={g.id}
                                      className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-200 px-2.5 py-1 text-xs font-medium"
                                    >
                                      {g?.nom}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-slate-500 dark:text-slate-400 text-sm">
                                    Aucun groupe
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 p-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            Observations
                          </div>
                          <p className="mt-1 text-sm text-slate-700 dark:text-slate-200 break-words">
                            {r.observations || "—"}
                          </p>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          {r.type_reglement === "En ligne" && (
                            <ButtonCBplus
                              onClick={() => navigate("/paiements")}
                              hoverLabel="Ajouter un paiement en ligne"
                            />
                          )}

                          <ButtonPrint
                            onClick={() =>
                              clubSettings && handleOpenAttestation(r)
                            }
                            hoverLabel={
                              clubSettings
                                ? "Attestation d'inscription (PDF)"
                                : "Paramètres en cours de chargement"
                            }
                            disabled={!clubSettings}
                          />

                          <ButtonEdit onClick={() => openEditModal(r)} />
                          <ButtonDelete onClick={() => handleDelete(r.id)} />
                        </div>
                      </article>
                    );
                  })}

                  {visibleCount < filtered.length && (
                    <div className="pt-2 text-center">
                      <button
                        type="button"
                        onClick={() =>
                          setVisibleCount((v) =>
                            Math.min(v + CHUNK, filtered.length),
                          )
                        }
                        className="rounded-xl px-4 py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100"
                      >
                        Charger plus ({visibleCount} / {filtered.length})
                      </button>
                    </div>
                  )}
                </div>

                {/* ================= DESKTOP ================= */}
                <div
                  ref={scrollRef}
                  onScroll={onScroll}
                  className="hidden lg:block overflow-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 max-h-[72vh]"
                >
                  <table className="w-full table-fixed">
                    <thead className="sticky top-0 z-20">
                      <tr className="bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur text-xs uppercase tracking-wide">
                        <th className="w-[18%] px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                          Adhérent
                        </th>
                        <th className="w-[12%] px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                          Statut
                        </th>
                        <th className="w-[14%] px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                          Saison / Groupes
                        </th>
                        <th className="w-[20%] px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                          Montants
                        </th>
                        <th className="w-[10%] px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                          Total
                        </th>

                        <th className="w-[12%] px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                          Paiement
                        </th>
                        <th className="w-[14%] px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                          Observations
                        </th>
                        <th className="w-[10%] px-4 py-3 text-right font-semibold text-slate-700 dark:text-white sticky right-0 z-20 bg-slate-50/95 dark:bg-slate-900/95">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                      {visibleReglements.map((r) => {
                        const a = r?.Adherent || {};
                        const total = calculateTotal(r);

                        return (
                          <tr
                            key={r.id}
                            className="align-top transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                          >
                            <td className="px-4 py-4">
                              <div className="font-semibold text-slate-900 dark:text-slate-100">
                                {a.nom || "—"} {a.prenom || ""}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              {editingStatusId === r.id ? (
                                <select
                                  autoFocus
                                  value={statusDraft || ""}
                                  disabled={savingStatusId === r.id}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setStatusDraft(v);
                                    saveStatus(r, v);
                                  }}
                                  onBlur={() => setEditingStatusId(null)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape")
                                      setEditingStatusId(null);
                                  }}
                                  className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs"
                                >
                                  <option value="">—</option>
                                  {STATUTS.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingStatusId(r.id);
                                    setStatusDraft(r.statut_reglement || "");
                                  }}
                                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold text-white ${getStatusClass(
                                    r.statut_reglement,
                                  )}`}
                                  title="Modifier le statut"
                                >
                                  {savingStatusId === r.id
                                    ? "…"
                                    : r.statut_reglement || "—"}
                                </button>
                              )}
                            </td>

                            <td className="px-4 py-4">
                              <div className="font-medium text-slate-800 dark:text-slate-100">
                                {r.saison || "—"}
                              </div>

                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {(a?.Groups || []).length ? (
                                  a.Groups.map((g) => (
                                    <span
                                      key={g.id}
                                      className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-200 px-2 py-0.5 text-[11px] font-medium"
                                    >
                                      {g?.nom}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-slate-500 dark:text-slate-400">
                                    Aucun groupe
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className="px-4 py-4">
                              <div className="space-y-1 text-slate-700 dark:text-slate-200 text-xs">
                                <div className="flex items-center justify-between gap-3">
                                  <span>Lic. + Ass.</span>
                                  <span className="font-medium">
                                    {fmtEUR(r.montant_lic_assurance)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span>Cotisation</span>
                                  <span className="font-medium">
                                    {fmtEUR(r.montant_cotisation)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span>Réduc. foyer</span>
                                  <span className="font-medium">
                                    {fmtEUR(r.reduction_foyer)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span>Pass’Sport</span>
                                  <span className="font-medium">
                                    {r.pass_sport !== null &&
                                    r.pass_sport !== undefined
                                      ? fmtEUR(r.pass_sport)
                                      : "N/A"}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span>Pass Club 32</span>
                                  <span className="font-medium">
                                    {r.passclub32 !== null && r.passclub32 !== undefined
                                      ? fmtEUR(r.passclub32)
                                      : "N/A"}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span>Réduc. saison</span>
                                  <span className="font-medium">
                                    {fmtEUR(r.reduction_saison)}
                                  </span>
                                </div>
                              </div>
                            </td>

                            <td className="px-4 py-4">
                              <div className="font-bold text-slate-900 dark:text-white">
                                {fmtEUR(total)}
                              </div>
                            </td>

                            <td className="px-4 py-4 text-slate-700 dark:text-slate-200">
                              <div className="font-medium">
                                {r.type_reglement || "—"}
                              </div>
                              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                {r.nb_reglement ?? "—"} règlement(s)
                              </div>
                            </td>

                            <td className="px-4 py-4">
                              <div
                                className="text-slate-700 dark:text-slate-200 text-sm break-words"
                                title={r.observations || ""}
                              >
                                {r.observations || "—"}
                              </div>
                            </td>

                            <td className="px-4 py-4 sticky right-0 z-10 bg-white dark:bg-slate-900/95">
                              <div className="flex flex-wrap justify-end gap-2">
                                {r.type_reglement === "En ligne" && (
                                  <ButtonCBplus
                                    onClick={() => navigate("/paiements")}
                                    hoverLabel="Ajouter un paiement en ligne"
                                  />
                                )}

                                <ButtonPrint
                                  onClick={() =>
                                    clubSettings && handleOpenAttestation(r)
                                  }
                                  hoverLabel={
                                    clubSettings
                                      ? "Attestation d'inscription (PDF)"
                                      : "Paramètres en cours de chargement"
                                  }
                                  disabled={!clubSettings}
                                />

                                <ButtonEdit onClick={() => openEditModal(r)} />
                                <ButtonDelete
                                  onClick={() => handleDelete(r.id)}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {visibleCount < filtered.length && (
                    <div className="p-3 text-center text-xs text-slate-500">
                      Affichage de {visibleCount} / {filtered.length} —
                      descendez pour charger la suite…
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      <CreateReglementInscriptionModal
        isModalOpen={isCreateModalOpen}
        setIsModalOpen={setIsCreateModalOpen}
        fetchReglements={fetchReglements}
        onClose={closeCreateModal}
      />
      <EditReglementInscriptionModal
        isModalOpen={isEditModalOpen}
        setIsModalOpen={setIsEditModalOpen}
        reglement={editableReglement}
        fetchReglements={fetchReglements}
        onClose={closeEditModal}
      />
    </section>
  );
}
